import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/hooks/use-auth";
import { Link } from "@tanstack/react-router";
import {
    LogOut,
    User
} from "lucide-react";
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

function Header() {
    const { user, signOut } = useAuth();

    const handleSignOut = async () => {
        try {
            await signOut();
        } catch (error) {
            console.error('Sign out error:', error);
        }
    };

    return <DropdownMenu>
        <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                <Avatar className="h-8 w-8">
                    <AvatarImage src={user?.user_metadata?.avatar_url} alt={user?.email} />
                    <AvatarFallback>
                        {user?.email?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                </Avatar>
            </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-56 z-[999]" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
                <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium leading-none">{user?.user_metadata?.full_name || 'User'}</p>
                    <p className="text-xs leading-none text-muted-foreground">
                        {user?.email}
                    </p>
                </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
                <Link to="/dashboard/settings">
                    <User className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleSignOut}>
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
            </DropdownMenuItem>
        </DropdownMenuContent>
    </DropdownMenu >
}

export default function SiteHeader() {
    const ref = useRef<any>(null);
    const [targetElement, setTargetElement] = useState<Element | null>(null);

    useEffect(() => {
        const applyDashboardMode = () => {
            if (ref.current) {
                ref.current.isDashboardDomain = true;
                ref.current.sitePath = '//plusefin.com';
                ref.current.pageUrl = window.location.pathname;
                ref.current.requestUpdate?.();
            }
        };

        const checkForElement = () => {
            applyDashboardMode();
            const $el = ref?.current?.shadowRoot?.querySelector('#account-actions');
            if ($el) {
                const $a = $el.querySelector('a');
                if ($a) {
                    $a.style.display = 'none';
                }
                setTargetElement($el);
            } else {
                setTimeout(checkForElement, 50);
            }
        };

        // Wait for the web component to be fully loaded
        if (ref.current) {
            if (ref.current.shadowRoot) {
                applyDashboardMode();
                checkForElement();
            } else {
                // Wait for shadow DOM to be created
                const observer = new MutationObserver(() => {
                    if (ref.current?.shadowRoot) {
                        observer.disconnect();
                        applyDashboardMode();
                        checkForElement();
                    }
                });
                observer.observe(ref.current, { childList: true, subtree: true });
            }
        }
    }, []);

    return (
        <>
            <site-header
                ref={ref}
                style={{ zIndex: 99, position: 'relative' }}
                sitepath="//plusefin.com"
                pageurl={location.pathname}
            />
            {targetElement && createPortal(<Header />, targetElement)}
        </>
    );
}
